import {useState, useEffect, useMemo, useCallback} from 'react'

import {useDispatch, useSelector} from 'react-redux'

import {debounce} from 'lodash'

import {
  // selectMap,
  selectLocationFilter,
  selectIsProgrammaticPan
  // userCoordinatesSelector
} from '../selectors'




function calculateViewportRadius(map) {
  // Get the bounds of the map
  const bounds = map.getBounds()
  const center = map.getCenter()


  // Get the northeast corner of the bounds
  const ne = bounds.getNorthEast()

  // Calculate the distance from the center to the northeast corner
  const radius = window.google.maps.geometry.spherical.computeDistanceBetween(center, ne)


  return radius * 0.00062137 // return radius in miles
}





export function useMaps() {

  const dispatch = useDispatch()

  const zoom = useSelector(state => state.map.zoom)

  const location = useSelector(selectLocationFilter)

  const [map, setMap] = useState(null)

  const isProgrammaticPan = useSelector(selectIsProgrammaticPan)
  // const coordinates = useSelector(userCoordinatesSelector)



  const debouncedSearch = useMemo(() => {
    return debounce((lat, lng) => {
      dispatch({type: 'UPDATE_CENTER_FILTER', payload: {lat, lng}})
      dispatch({type: 'NEW_SEARCH_REQUESTED'})
    }, 300)
  }, [dispatch])




  const searchArea = useCallback(() => {
    const newCenter = map?.getCenter()

    if (!newCenter) return

    const currentCenter = new window.google.maps.LatLng(location.lat, location.lng)

    const radius = window.google.maps.geometry.spherical.computeDistanceBetween(newCenter, currentCenter)

    // Only do a new search if a user pans over one mile and it wasn't done programmatically.
    // An example of a programatic pan is when a user clicks on a venue in MapListItem
    if (radius * 0.00062137 > 1 && !isProgrammaticPan) {
      const lat = newCenter.lat()
      const lng = newCenter.lng()

      debouncedSearch(lat,lng)
    }

    if (isProgrammaticPan) {
      dispatch({type: 'RESET_PROGRAMMATIC_PAN'});
    }
  }, [
    map,
    dispatch, 
    location.lat, 
    location.lng, 
    debouncedSearch,
    isProgrammaticPan
  ])




  // Attach a listener to the map to listen to search a new area if a user
  // pans or moves the map to a new area
  useEffect(() => {
    if (!map) return

    const listener = window.google.maps.event.addListener(map, 'idle', searchArea)

    return () => listener?.remove()
  }, [map, searchArea])



  // This updates the search radius based on map zoom but does not activate the filter
  useEffect(() => {
    if (!map) return

    const handleZoomChange = () => {
      const newZoom = map.getZoom()
      const radius = calculateViewportRadius(map)
      dispatch({type: 'UPDATE_RADIUS_FILTER_BUT_DONT_ACTIVATE', payload: radius})
      dispatch({type: 'UPDATE_ZOOM', payload: newZoom})
    }

    const zoomListener = map.addListener('zoom_changed', handleZoomChange)

    return () => {
      zoomListener.remove()
    }
  }, [map, dispatch])





  useEffect(() => {
    // If a map is loaded then no need to continue
    if (map) return

    // If there are no coordinates then also return
    if (!location.lat || !location.lng) return


    const container = document.getElementById('map-container')

    // Load map
    initMap()


    async function initMap() {
      try {
        // eslint-disable-next-line no-unused-vars
        const {Map} = await window.google.maps.importLibrary('maps')
        // eslint-disable-next-line no-unused-vars
        const {LatLng} = await window.google.maps.importLibrary('core')
        // eslint-disable-next-line no-unused-vars
        const {Geometry} = await window.google.maps.importLibrary('geometry')

        const latlng = new LatLng(location.lat, location.lng)


        const promise = new Map(container, {
          mapId: 'e7b4ef4a2bef2367',
          zoom: zoom,
          maxZoom: 21,
          minZoom: 4,
          center: latlng,
          mapTypeControl: false,
          fullscreenControl: false,
          streetViewControl: false,
          zoomControl: false,
          gestureHandling: 'greedy',
          clickableIcons: false
        })

        const gmap = await promise

        // Save the map
        setMap(gmap)
        dispatch({type: 'SET_MAP', payload: gmap})


        // Listen for when a map is panned
        window.google.maps.event.addListenerOnce(gmap, 'tilesloaded', function() {
          const viewportRadius = calculateViewportRadius(gmap)
          dispatch({type: 'SET_RADIUS_FILTER', payload: viewportRadius})
        })
      } catch (error) {
        console.error('Error loading Google Maps:', error)
      }
    }
  }, [map, zoom, dispatch, location.lat, location.lng])


  return map
}




export default useMaps



